import { useCallback } from 'react'
import { requestUpdateSite, requestUpdateEditOnlySite } from 'api/site'
import { SITE_STATUS_EDIT_ONLY_ID } from 'constants/siteStatuses'

/**
 * Returns a saveSiteData function that routes to the correct API endpoint
 * depending on the site's current status.
 *
 * Eliminates the if/else block that was duplicated in every field handler
 * across About and Details:
 *
 *   Before (×20 in Details alone):
 *     if (site.SiteStatusId === SITE_STATUS_EDIT_ONLY_ID) {
 *       await requestUpdateEditOnlySite(site.id, data)
 *     } else {
 *       await requestUpdateSite(params.id, historyId, data)
 *     }
 *
 *   After:
 *     await saveSiteData({ fieldKey: value })
 *
 * @param {object} site       - site object from SiteContext
 * @param {object} params     - route params from useParams()
 * @param {string|number} historyId - resolved history ID for this site context
 *
 * @returns {{ saveSiteData: (data: object, editOnlyExtra?: object) => Promise<void> }}
 *
 * editOnlyExtra — additional fields merged into the payload only when calling
 * requestUpdateEditOnlySite (e.g. { proposalPurpose: 8 } on the About page).
 */
export function useDelayedSiteUpdate({ site, params, historyId }) {
  const saveSiteData = useCallback(
    async (data, editOnlyExtra = {}, options = {}) => {
      const { requireSiteHistoryForEditOnly = false } = options
      const canUseEditOnlyEndpoint = !requireSiteHistoryForEditOnly || Boolean(params.siteHistoryId)

      if (site.SiteStatusId === SITE_STATUS_EDIT_ONLY_ID && canUseEditOnlyEndpoint) {
        return requestUpdateEditOnlySite(site.id, { ...data, ...editOnlyExtra })
      }
      return requestUpdateSite(params.id, historyId, data)
    },
    [site.SiteStatusId, site.id, params.id, params.siteHistoryId, historyId]
  )

  return { saveSiteData }
}
